import { useEffect, useState } from "react";
import { Link, useNavigation } from "../app/navigation";
import { useWorkspace } from "../app/workspace";
import { AppShell } from "../components/AppShell";
import { PublicHeader } from "../components/PublicHeader";
import { login, readGuardianLink } from "../lib/api";
import type { Role, SupportCase } from "../domain/types";
import { redactSupportText } from "../domain/supportAutomation";
import {
  CANONICAL_ORIGIN,
  OPERATOR_NAME,
  POLICY_EFFECTIVE_DATE,
  POLICY_VERSION,
  SUPPORT_EMAIL,
} from "../legal/policy";

const supportArticles = [
  { id: "offline", title: "오프라인 출결이 전송 대기일 때", body: "연결이 돌아오면 같은 저장 키로 다시 보냅니다. 다른 기기 값과 다르면 두 값을 보여준 뒤 직접 선택합니다." },
  { id: "billing", title: "입금 또는 환불을 잘못 기록했을 때", body: "수납 대조의 영수증에서 이유와 함께 되돌리세요. 원래 영수증은 지우지 않고 보정 영수증을 연결합니다." },
  { id: "export", title: "전체 기록을 가져가고 싶을 때", body: "데이터와 내보내기에서 구독 상태와 무관하게 전체 기록 파일을 받을 수 있습니다." },
  { id: "roles", title: "강사에게 특정 반만 보이게 할 때", body: "팀과 권한에서 강사 역할과 담당 수업을 함께 지정합니다. 서버도 같은 범위를 다시 검사합니다." },
  { id: "guardian", title: "보호자 확인 링크가 만료됐을 때", body: "문구를 다시 확인한 뒤 새 72시간 목적 제한 링크를 만드세요. 학생 이름이나 연락처는 URL에 넣지 않습니다." },
] as const;

export function SignInPage() {
  const { adoptSession, state } = useWorkspace();
  const { navigate } = useNavigation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const result = await login({ email, password });
      adoptSession({ ...result, mode: "user" });
      navigate("/close/today");
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "로그인하지 못했어요.",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <PublicFrame>
      <main className="auth-page">
        <section className="auth-story">
          <p>강사는 지금 수업만, 원장은 남은 일만</p>
          <h1>다시 운영할 자리로 들어갑니다.</h1>
          <ol>
            <li>
              <span>01</span>권한에 맞는 반과 기록만 표시
            </li>
            <li>
              <span>02</span>저장 충돌과 외부 효과를 영수증으로 확인
            </li>
            <li>
              <span>03</span>전체 데이터는 언제든 내보내기
            </li>
          </ol>
        </section>
        <form className="auth-form" onSubmit={(event) => void submit(event)}>
          <header>
            <p>수업마침 계정</p>
            <h2>로그인</h2>
          </header>
          {state ? (
            <p className="inline-success">
              이미 운영 화면이 열려 있어요.{" "}
              <Link href="/close/today">운영 화면으로 이동</Link>
            </p>
          ) : null}
          {error ? (
            <p className="inline-error" role="alert">
              {error}
            </p>
          ) : null}
          <label>
            이메일
            <input
              type="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </label>
          <label>
            비밀번호
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </label>
          <button type="submit" className="primary-action" disabled={busy}>
            {busy ? "확인 중…" : "로그인"}
          </button>
          <p>
            처음 시작한다면 <Link href="/start">학원 운영 자리 만들기</Link>에서
            가입 가능 상태를 확인할 수 있어요.
          </p>
        </form>
      </main>
    </PublicFrame>
  );
}

export function DiagnosisPage() {
  const questions = [
    ["출결을 다른 장부에 다시 옮겨 적나요?", "duplicate"],
    ["결석 뒤 보강 여부를 카톡에서 다시 찾나요?", "makeup"],
    ["부분 입금과 환불을 월말에 한꺼번에 맞추나요?", "billing"],
    ["강사 수당 근거가 실제 수업과 떨어져 있나요?", "payroll"],
    ["하루가 끝나도 처리하지 않은 일이 몇 건인지 모르나요?", "close"],
    ["학생 명단을 무료로 내보내기 어렵나요?", "export"],
  ] as const;
  const [answers, setAnswers] = useState<Record<string, boolean>>({});
  const answered = Object.keys(answers).length;
  const score = Object.values(answers).filter(Boolean).length;
  const result = answered === questions.length;
  const { beginSample } = useWorkspace();
  const { navigate } = useNavigation();
  async function sample() {
    await beginSample();
    navigate("/try/today");
  }
  return (
    <PublicFrame>
      <main className="diagnosis-page">
        <header>
          <p>이메일 없이 바로 결과</p>
          <h1>우리 학원의 ‘수업 뒤 남은 일’을 세어보세요.</h1>
          <span>6문항 · 약 1분 · 답변은 서버에 저장하지 않습니다.</span>
        </header>
        <section className="diagnosis-sheet">
          {questions.map(([question, id], index) => (
            <fieldset key={id}>
              <legend>
                <span>{String(index + 1).padStart(2, "0")}</span>
                {question}
              </legend>
              <label>
                <input
                  type="radio"
                  name={id}
                  checked={answers[id] === true}
                  onChange={() => setAnswers({ ...answers, [id]: true })}
                />{" "}
                예, 자주 그래요
              </label>
              <label>
                <input
                  type="radio"
                  name={id}
                  checked={answers[id] === false}
                  onChange={() => setAnswers({ ...answers, [id]: false })}
                />{" "}
                아니요
              </label>
            </fieldset>
          ))}
        </section>
        {result ? (
          <section className="diagnosis-result" aria-live="polite">
            <div>
              <span>겹쳐 있는 운영 흐름</span>
              <strong>
                {score}
                <small>/ 6</small>
              </strong>
            </div>
            <article>
              <h2>
                {score >= 4
                  ? "출결 뒤 후속을 한 사건으로 묶을 여지가 큽니다."
                  : score >= 2
                    ? "일부 후속만 연결해도 마감 시간이 줄 수 있습니다."
                    : "현재 방식이 잘 맞는지 샘플 한 번으로 확인하세요."}
              </h2>
              <p>
                {score
                  ? "선택한 문제를 위험 점수로 바꾸지 않습니다. 실제 운영일에서 “남은 일 0”까지 걸리는 시간으로 다시 판단하세요."
                  : "도구를 바꾸는 비용도 큽니다. 현재 방식보다 분명히 빨라지는지 먼저 비교하세요."}
              </p>
              <button
                type="button"
                className="primary-action"
                onClick={() => void sample()}
              >
                같은 흐름을 샘플로 확인
              </button>
            </article>
          </section>
        ) : (
          <p className="diagnosis-progress">
            {answered}/6 답변 · 모두 답하면 이 화면에서 바로 결과를
            보여드립니다.
          </p>
        )}
      </main>
    </PublicFrame>
  );
}

export function PricingPage() {
  return (
    <PublicFrame>
      <main className="public-content">
        <header className="public-title">
          <p>현재 이용 기준</p>
          <h1>
            결제 없이 운영을 시작하고
            <br />
            자기 기록은 계속 가져갑니다.
          </h1>
          <span>
            초기 공개 운영에는 판매 상품·구매 버튼·자동 갱신이 없습니다.
          </span>
        </header>
        <section className="public-plan-grid">
          <article>
            <span>핵심 운영</span>
            <h2>결제 없이 사용</h2>
            <ul>
              <li>출결·보강·오늘 마침</li>
              <li>수동 수납 대조</li>
              <li>강사 수당 후보</li>
            </ul>
          </article>
          <article>
            <span>데이터 권리</span>
            <h2>잠그지 않는 기능</h2>
            <ul>
              <li>전체 기록 내보내기</li>
              <li>가져오기 되돌리기</li>
              <li>계정·학원 삭제 요청</li>
            </ul>
          </article>
          <article>
            <span>명확한 경계</span>
            <h2>현재 비활성</h2>
            <ul>
              <li>실제 결제·자동 갱신</li>
              <li>보호자 메시지 자동 발송</li>
              <li>외부 상담 공급자 전달</li>
            </ul>
          </article>
        </section>
        <section className="pricing-boundary">
          <h2>결제가 연결되기 전 지키는 원칙</h2>
          <div>
            <strong>초기 공개 기간 무료</strong>
            <p>결제 공급자를 연결하기 전에는 사용 기간 만료로 운영을 막지 않습니다.</p>
          </div>
          <div>
            <strong>내보내기 잠금 없음</strong>
            <p>향후 유료 기능이 생겨도 자기 기록을 가져가는 기능은 유지합니다.</p>
          </div>
          <div>
            <strong>공급자 상태 fail-closed</strong>
            <p>
              영수증이 없거나 불일치하면 사용권을 임의로 활성화하지 않습니다.
            </p>
          </div>
          <Link className="primary-action" href="/">
            샘플 하루부터 확인
          </Link>
        </section>
      </main>
    </PublicFrame>
  );
}

export function SupportPage() {
  const { state, command, mutating } = useWorkspace();
  if (!state) return <PublicSupport />;
  return (
    <SupportWorkspace state={state} command={command} mutating={mutating} />
  );
}

function SupportWorkspace({
  state,
  command,
  mutating,
}: {
  state: NonNullable<ReturnType<typeof useWorkspace>["state"]>;
  command: ReturnType<typeof useWorkspace>["command"];
  mutating: boolean;
}) {
  const [category, setCategory] = useState<SupportCase["category"]>("how_to");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [kbQuery, setKbQuery] = useState("");
  const [redaction, setRedaction] = useState(true);
  const risky = [
    "billing",
    "privacy",
    "security",
    "data_loss",
    "child",
    "access",
    "legal",
  ].includes(category);
  const preview = redactSupportText(body);
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    await command({
      type: "support.create",
      category,
      subject,
      body,
      route: window.location.pathname,
      requestId: crypto.randomUUID(),
    });
    setSubject("");
    setBody("");
  }
  const clusters = Object.values(
    state.supportCases.reduce<
      Record<string, { key: string; count: number; severity: string }>
    >((result, item) => {
      const entry = result[item.clusterKey] ?? {
        key: item.subject,
        count: 0,
        severity: item.severity,
      };
      entry.count += 1;
      result[item.clusterKey] = entry;
      return result;
    }, {}),
  );
  const visibleArticles = supportArticles.filter((article) =>
    `${article.title} ${article.body}`
      .toLocaleLowerCase("ko-KR")
      .includes(kbQuery.trim().toLocaleLowerCase("ko-KR")),
  );
  return (
    <AppShell
      title="도움 요청"
      description="공급자가 멈춰도 문의는 제품 안에 먼저 저장합니다"
    >
      <section className="support-kb" aria-labelledby="support-kb-title">
        <header>
          <div><p>검토된 도움말</p><h2 id="support-kb-title">먼저 해결 방법 찾기</h2></div>
          <label>도움말 검색<input type="search" value={kbQuery} onChange={(event) => setKbQuery(event.target.value)} placeholder="오프라인, 수납, 권한…" /></label>
        </header>
        {visibleArticles.length ? (
          <div>{visibleArticles.map((article) => <details key={article.id}><summary>{article.title}</summary><p>{article.body}</p></details>)}</div>
        ) : <p>맞는 도움말이 없어요. 아래 문의에 상황을 남겨 주세요.</p>}
      </section>
      <section className="support-layout">
        <form className="support-form" onSubmit={(event) => void submit(event)}>
          <header>
            <p>상황 정보 자동 첨부</p>
            <h2>운영 중 막힌 일을 알려주세요.</h2>
            <span>
              현재 화면·빌드·요청 ID는 붙이고 학생명·연락처·금액은 기본으로
              가립니다.
            </span>
          </header>
          <label>
            분류
            <select
              value={category}
              onChange={(event) =>
                setCategory(event.target.value as SupportCase["category"])
              }
            >
              <option value="how_to">사용 방법</option>
              <option value="bug">기능 오류</option>
              <option value="billing">수납·결제</option>
              <option value="privacy">개인정보</option>
              <option value="security">보안</option>
              <option value="data_loss">기록 손실</option>
              <option value="child">아동 관련</option>
              <option value="access">권한·접근</option>
              <option value="legal">법적 문의</option>
            </select>
          </label>
          <label>
            제목
            <input
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
              placeholder="무엇을 하다가 막혔나요?"
            />
          </label>
          <label>
            상황
            <textarea
              value={body}
              onChange={(event) => setBody(event.target.value)}
              placeholder="기대한 결과와 실제 결과, 다시 생기는 순서를 적어 주세요."
            />
          </label>
          <label className="check-line">
            <input
              type="checkbox"
              checked={redaction}
              onChange={(event) => setRedaction(event.target.checked)}
            />{" "}
            저장 전에 민감정보 가림 결과 확인
          </label>
          {redaction && body ? (
            <div className="redaction-preview">
              <strong>저장될 문의</strong>
              <p>{preview}</p>
            </div>
          ) : null}
          {risky ? (
            <p className="human-required">
              이 분류는 수량과 관계없이 사람이 직접 확인하도록 표시합니다.
            </p>
          ) : null}
          <button
            type="submit"
            className="primary-action"
            disabled={
              mutating || subject.trim().length < 3 || body.trim().length < 5
            }
          >
            {mutating ? "제품 안에 저장 중…" : "문의 접수"}
          </button>
        </form>
        <aside className="support-history">
          <header>
            <span>내 문의</span>
            <strong>{state.supportCases.length}</strong>
          </header>
          {state.supportCases.length ? (
            <ol>
              {state.supportCases.toReversed().map((item) => (
                <li key={item.id}>
                  <span
                    className={`case-severity case-severity--${item.severity}`}
                  >
                    {item.severity}
                  </span>
                  <div>
                    <strong>{item.subject}</strong>
                    <small>
                      {caseState(item.state)} · 공급자{" "}
                      {receiptState(item.providerState)}
                    </small>
                  </div>
                  <b>{item.id.slice(-8)}</b>
                </li>
              ))}
            </ol>
          ) : (
            <p>아직 접수한 문의가 없어요.</p>
          )}
          {clusters.some((item) => item.count >= 3) ? (
            <div className="cluster-notice">
              <strong>반복 문의 후보</strong>
              <p>
                서로 다른 운영처와 재현 근거가 확인되기 전 자동 제품 변경은 하지
                않습니다.
              </p>
            </div>
          ) : null}
        </aside>
      </section>
    </AppShell>
  );
}

function PublicSupport() {
  return (
    <PublicFrame>
      <main className="public-content">
        <header className="public-title">
          <p>공개 고객지원</p>
          <h1>
            막힌 화면과 요청 번호만
            <br />
            안전하게 알려주세요.
          </h1>
          <span>
            로그인한 원장은 제품 안에 문의를 남길 수 있고, 로그인할 수 없는 경우
            검증된 공용 지원 메일을 이용할 수 있습니다.
          </span>
        </header>
        <section className="support-principles">
          <article>
            <span>P0</span>
            <h2>보안·개인정보·기록 손실</h2>
            <p>
              자동 답변이나 자동 종결 없이 사람이 확인해야 하는 건으로
              분류합니다.
            </p>
          </article>
          <article>
            <span>P1–P2</span>
            <h2>수납·권한·기능 차단</h2>
            <p>
              현재 화면과 요청 ID는 붙이고 학생명·전화·자유 메모는 기본
              제외합니다.
            </p>
          </article>
          <article>
            <span>P3</span>
            <h2>사용 방법·개선 제안</h2>
            <p>
              승인된 도움말만 제안하며 원문 문의에서 바로 제품을 바꾸지
              않습니다.
            </p>
          </article>
        </section>
        <section className="support-entry">
          <h2>{SUPPORT_EMAIL}</h2>
          <p>
            제품명, 문제가 생긴 화면, 요청 번호, 기대한 결과와 실제 결과를 적어
            주세요. 학생 이름·보호자 연락처·비밀번호·결제정보·보호자 확인 링크
            전체는 메일에 넣지 마세요.
          </p>
          <a
            className="primary-action"
            href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("[수업마침 문의]")}`}
          >
            메일로 문의
          </a>
          <p>
            외부 상담 위젯과 자동 답변은 아직 연결하지 않았습니다. 개인정보·보안·
            기록 손실·아동·권한·법적 문의는 자동 종결하지 않고 사람이 확인합니다.
          </p>
        </section>
      </main>
    </PublicFrame>
  );
}

export function GuardianLinkPage({ token }: { token: string }) {
  const [result, setResult] = useState<Awaited<
    ReturnType<typeof readGuardianLink>
  > | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    void readGuardianLink(token)
      .then((value) => {
        if (active) setResult(value);
      })
      .catch((caught) => {
        if (active)
          setError(
            caught instanceof Error
              ? caught.message
              : "링크를 확인하지 못했어요.",
          );
      });
    return () => {
      active = false;
    };
  }, [token]);
  return (
    <div className="guardian-page">
      <header>
        <Link href="/" className="guardian-brand">
          수업마침
        </Link>
        <Link href="/privacy">개인정보 안내</Link>
      </header>
      <main>
        {error ? (
          <section className="guardian-document guardian-document--error">
            <p>확인할 수 없는 링크</p>
            <h1>{error}</h1>
            <span>
              학생 정보는 표시하지 않았습니다. 학원에 새 링크를 요청해 주세요.
            </span>
          </section>
        ) : !result ? (
          <section className="guardian-document" aria-busy="true">
            <p>확인 링크</p>
            <h1>안내를 안전하게 여는 중</h1>
          </section>
        ) : (
          <section className="guardian-document">
            <p>
              {result.payload.academyName}에서 보낸{" "}
              {purposeLabel(result.purpose)} 안내
            </p>
            <h1>{result.payload.studentLabel} 학생 안내</h1>
            <blockquote>{result.payload.body}</blockquote>
            <dl>
              <div>
                <dt>발급</dt>
                <dd>
                  {new Intl.DateTimeFormat("ko-KR", {
                    dateStyle: "medium",
                    timeStyle: "short",
                  }).format(new Date(result.payload.issuedAt))}
                </dd>
              </div>
              <div>
                <dt>만료</dt>
                <dd>
                  {new Intl.DateTimeFormat("ko-KR", {
                    dateStyle: "medium",
                    timeStyle: "short",
                  }).format(new Date(result.expiresAt))}
                </dd>
              </div>
              <div>
                <dt>남은 확인</dt>
                <dd>{result.remainingUses}회</dd>
              </div>
            </dl>
            <p className="guardian-boundary">
              이 링크는 안내 확인에만 쓰이며 학생 전체 기록·다른 보호자·학원
              운영 화면에는 접근할 수 없습니다.
            </p>
          </section>
        )}
      </main>
      <footer>
        문의는 안내를 보낸 학원에 연락해 주세요. URL에는 이름·전화번호를 넣지
        않습니다.
      </footer>
    </div>
  );
}

export function PrivacyPage() {
  return <PolicyPage kind="privacy" />;
}
export function TermsPage() {
  return <PolicyPage kind="terms" />;
}

function PolicyPage({ kind }: { kind: "privacy" | "terms" }) {
  const privacy = kind === "privacy";
  return (
    <PublicFrame>
      <main className="policy-page">
        <header>
          <p>시행 {POLICY_EFFECTIVE_DATE} · 버전 {POLICY_VERSION}</p>
          <h1>{privacy ? "개인정보 처리방침" : "이용약관"}</h1>
          <span>
            운영자 {OPERATOR_NAME} · 문의 {SUPPORT_EMAIL}
          </span>
        </header>
        {privacy ? (
          <>
            <PolicySection number="01" title="처리 목적과 역할">
              <p>
                {OPERATOR_NAME}는 수업마침의 계정·학원 운영·보안·문의 기능을 제공하기
                위해 개인정보를 처리합니다. 학원 운영자가 학생·보호자·구성원
                정보를 입력하는 경우 학원은 그 정보의 수집 근거와 안내를 책임지고,
                수업마침은 서비스 제공에 필요한 범위에서 처리합니다.
              </p>
            </PolicySection>
            <PolicySection number="02" title="처리하는 항목">
              <p>
                가입 시 학원명, 원장 이메일, 단방향 변환된 비밀번호, 정책 버전과
                동의 시각을 처리합니다. 운영 중에는 구성원 역할·담당 범위, 학생
                표시 이름·학년·반, 출결·보강·진도·과제, 보호자 연락처 표식과 안내
                초안, 수동 청구·입금·환불 기록, 강사 수당 후보, 마감·변경 영수증,
                가려진 문의를 처리합니다. 카드번호·계좌번호·주민등록번호 원문은
                수집하도록 설계하지 않았습니다.
              </p>
            </PolicySection>
            <PolicySection number="03" title="자동 생성 정보와 쿠키">
              <p>
                로그인·샘플 상태를 위한 필수 쿠키, 세션·CSRF 토큰의 해시, 접속 IP의
                일방향 해시와 요청 횟수 구간, 요청 번호, 가명 학원 ID, 기능 이벤트,
                빌드 버전과 시각을 처리합니다. 광고 식별자, 맞춤형 광고 쿠키,
                외부 분석 SDK는 사용하지 않습니다.
              </p>
            </PolicySection>
            <PolicySection number="04" title="처리위탁과 국외 이전">
              <p>
                서비스 실행·데이터베이스·콘텐츠 전송·트래픽 보호를 Cloudflare,
                Inc.(미국, privacyquestions@cloudflare.com)에 위탁합니다. 위 항목은
                서비스 이용 시 TLS로 미국과 Cloudflare의 글로벌 처리 지역에
                이전될 수 있습니다. 현재 D1 주 실행 지역은 APAC으로 확인되지만
                특정 국가에 고정된 jurisdiction은 아닙니다. 이전 목적은 서비스
                제공과 보안이며, 활성 데이터는 아래 보유기간까지, 복구 이력은
                삭제 뒤 최대 30일 범위에서만 유지됩니다. 국외 처리를 원하지 않으면
                가입하지 않거나 삭제를 요청할 수 있으나 서비스 제공은 어렵습니다.
              </p>
            </PolicySection>
            <PolicySection number="05" title="보유기간과 파기">
              <p>
                계정·학원 운영 데이터는 이용 중 보관합니다. 원장이 삭제를 요청하면
                30일 동안 취소할 수 있고, 예정일이 지나면 활성 계정·학원·구성원·
                운영·문의·분석 데이터를 삭제합니다. Cloudflare D1의 재해 복구
                이력에서는 최대 30일 뒤 복구할 수 없게 됩니다. 샘플은 12시간,
                세션은 12시간, 지원 개선 후보는 180일, 가명 기능 이벤트는 최대
                13개월 보관합니다. 법령상 별도 보존 의무가 실제로 생기면 해당
                항목만 분리하고 근거·항목·기간을 안내합니다.
              </p>
            </PolicySection>
            <PolicySection number="06" title="제3자 제공과 아동 정보">
              <p>
                현재 개인정보를 제3자에게 제공하지 않으며 결제·보호자 메시지·외부
                상담 공급자는 비활성입니다. 서비스 계정은 학원을 운영하도록 권한을
                받은 성인만 만들 수 있습니다. 학원이 학생 등 제3자의 정보를
                입력할 때에는 필요한 동의·고지·권한을 확보하고 최소한만 입력해야
                합니다. 민감정보와 고유식별정보는 입력하지 마세요.
              </p>
            </PolicySection>
            <PolicySection number="07" title="열람·정정·내보내기·삭제">
              <p>
                원장은 설정의 데이터 화면에서 전체 기록과 계정 동의 정보를 JSON으로
                내보내고 학원 데이터 삭제를 요청하거나 예정일 전 취소할 수 있습니다.
                그 밖의 열람·정정·삭제·처리정지 요청은 {SUPPORT_EMAIL}로 접수합니다.
                학생·보호자·구성원은 소속 학원을 통해 먼저 요청할 수 있고, 본인
                확인이 필요한 경우 필요한 최소 정보만 추가로 요청합니다.
              </p>
            </PolicySection>
            <PolicySection number="08" title="안전조치와 자동화된 결정">
              <p>
                TLS, 비밀번호 단방향 변환, 세션 토큰 해시, 동일 출처·CSRF 확인,
                입력 검증, 요청 제한, 학원 격리, 역할·담당 범위 확인, 변경 영수증을
                사용합니다. 정보주체의 권리·의무에 중대한 영향을 미치는 완전 자동
                결정을 하지 않으며, 보안·개인정보·기록 손실·아동·권한·법적 문의는
                자동 종결하지 않습니다.
              </p>
            </PolicySection>
            <PolicySection number="09" title="보호 책임과 권리구제">
              <p>
                개인정보 보호업무와 고충은 {OPERATOR_NAME} 운영자가 담당하며
                {" "}<a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>로 접수합니다.
                개인정보침해신고센터 118, 개인정보분쟁조정위원회 1833-6972 등에도
                상담·분쟁조정을 신청할 수 있습니다.
              </p>
            </PolicySection>
            <PolicySection number="10" title="방침 변경 이력" id="policy-history">
              <p>
                버전 {POLICY_VERSION}은 {POLICY_EFFECTIVE_DATE}에 최초 시행했습니다.
                중요한 변경은 시행 7일 전, 권리에 중대한 불리한 변경은 30일 전에
                서비스에서 알리고 필요한 경우 다시 동의를 받습니다. 이전 버전은
                이 절에 계속 남깁니다.
              </p>
            </PolicySection>
          </>
        ) : (
          <>
            <PolicySection number="01" title="목적과 서비스 범위">
              <p>
                수업마침은 공부방·교습소·학원의 출결과 뒤따르는 운영 기록을
                연결하고 하루·주·월 마감 영수증을 만드는 도구입니다. 교육 성과,
                학생 위험, 법적 급여·노무·세무 판단, 메시지 전달, 입금·송금을
                보장하거나 대신하지 않습니다.
              </p>
            </PolicySection>
            <PolicySection number="02" title="계약과 계정">
              <p>
                학원을 대표하거나 운영 권한을 받은 성인이 약관과 개인정보
                처리방침에 동의하고 가입하면 이용계약이 성립합니다. 이용자는 계정과
                구성원 권한을 정확하게 관리하고 도용·분실을 발견하면 즉시
                {" "}{SUPPORT_EMAIL}로 알려야 합니다.
              </p>
            </PolicySection>
            <PolicySection number="03" title="현재 무료 이용과 향후 유료 기능">
              <p>
                초기 공개 기간에는 판매 상품·결제수단·자동 갱신 없이 핵심 운영
                기능을 제공합니다. 향후 유료 기능을 열 때에는 가격, 세금 포함 여부,
                제공 범위, 이용기간, 갱신·해지·환불 조건을 구매 전에 별도로 고지하고
                명시적 동의를 받습니다. 현재 화면에는 구매 계약이나 청구가 없습니다.
              </p>
            </PolicySection>
            <PolicySection number="04" title="입력 정보와 데이터 권리">
              <p>
                이용자는 학생·보호자·구성원 정보를 처리할 적법한 권한과 안내 책임을
                가지며 필요한 최소 정보만 입력해야 합니다. 저장 성공 영수증이 없는
                외부 효과는 완료로 보지 않습니다. 원장은 전체 기록을 내보내고
                30일 취소 기간을 둔 학원 데이터 삭제를 요청할 수 있습니다.
              </p>
            </PolicySection>
            <PolicySection number="05" title="금지 행위">
              <p>
                타인의 정보 도용, 권한 없는 개인정보 입력·공개, 불법 콘텐츠,
                서비스 방해·자동 대량 요청, 보안 기능 우회, 다른 학원 데이터 접근을
                금지합니다. 위반이나 긴급한 보안 위험이 있으면 이유와 복구·이의
                경로를 알리고 필요한 범위에서 이용을 제한할 수 있습니다.
              </p>
            </PolicySection>
            <PolicySection number="06" title="변경·중단·해지">
              <p>
                유지보수·보안 사고·공급자 장애 때 기능을 제한할 수 있습니다. 중요한
                변경이나 종료는 가능한 범위에서 30일 전에 알리고 내보내기 경로를
                제공합니다. 이용자는 언제든 삭제를 요청해 계약을 종료할 수 있으며
                예정일 전에는 요청을 취소할 수 있습니다.
              </p>
            </PolicySection>
            <PolicySection number="07" title="책임의 범위">
              <p>
                서비스는 이용자가 입력한 사실과 설정한 규칙을 정리하는 운영 도구이며
                공적 증명이나 전문가 판단이 아닙니다. 운영자의 고의 또는 중대한
                과실로 발생한 책임을 배제하지 않습니다. 이용자 입력 오류나 이용자
                권한 밖의 정보 처리로 생긴 결과는 이용자가 확인하고 바로잡아야 합니다.
              </p>
            </PolicySection>
            <PolicySection number="08" title="준거법·문의·변경 이력" id="terms-history">
              <p>
                대한민국 법을 준거법으로 하며 관할은 민사소송법에 따릅니다. 문의와
                이의 제기는 <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>로
                접수합니다. 버전 {POLICY_VERSION}은 {POLICY_EFFECTIVE_DATE}에 최초
                시행했습니다.
              </p>
            </PolicySection>
          </>
        )}
      </main>
    </PublicFrame>
  );
}

export function StatusPage() {
  const [health, setHealth] = useState<{
    api: string;
    database: string;
    revision: string;
    checkedAt: string;
  }>({
    api: "확인 중",
    database: "확인 중",
    revision: "—",
    checkedAt: new Date().toISOString(),
  });
  useEffect(() => {
    let active = true;
    Promise.all([
      fetch("/healthz").then(
        (response) =>
          response.json() as Promise<{ status: string; revision: string }>,
      ),
      fetch("/readyz").then(async (response) => ({
        ok: response.ok,
        body: (await response.json()) as { database?: string },
      })),
    ])
      .then(([api, ready]) => {
        if (active)
          setHealth({
            api: api.status === "ok" ? "정상" : "점검 필요",
            database: ready.ok
              ? (ready.body.database ?? "연결됨")
              : "점검 필요",
            revision: api.revision,
            checkedAt: new Date().toISOString(),
          });
      })
      .catch(() => {
        if (active)
          setHealth((current) => ({
            ...current,
            api: "닿지 않음",
            database: "확인 불가",
            checkedAt: new Date().toISOString(),
          }));
      });
    return () => {
      active = false;
    };
  }, []);
  return (
    <PublicFrame>
      <main className="status-page">
        <header>
          <span
            className={
              health.api === "정상" ? "status-dot is-ok" : "status-dot"
            }
            aria-hidden="true"
          />
          <div>
            <p>현재 실행 상태</p>
            <h1>
              {health.api === "정상" && health.database !== "점검 필요"
                ? "핵심 API가 응답하고 있습니다."
                : "일부 상태를 확인하고 있습니다."}
            </h1>
          </div>
        </header>
        <section>
          <div>
            <span>웹·API</span>
            <strong>{health.api}</strong>
          </div>
          <div>
            <span>운영 데이터 저장소</span>
            <strong>{health.database}</strong>
          </div>
          <div>
            <span>빌드 revision</span>
            <strong>{health.revision}</strong>
          </div>
          <div>
            <span>확인 시각</span>
            <strong>
              {new Intl.DateTimeFormat("ko-KR", {
                dateStyle: "medium",
                timeStyle: "medium",
              }).format(new Date(health.checkedAt))}
            </strong>
          </div>
        </section>
        <p>
          이 화면은 현재 요청과 저장소 연결만 확인합니다. 출시, 모니터링, 사용자
          가치 또는 메시지·결제 공급자 상태를 대신 증명하지 않습니다.
        </p>
      </main>
    </PublicFrame>
  );
}

export function NotFoundPage() {
  return (
    <PublicFrame>
      <main className="not-found">
        <p>404</p>
        <h1>이 장은 찾을 수 없어요.</h1>
        <h2>주소가 바뀌었거나 확인 권한이 없는 화면입니다.</h2>
        <p>대상 기록의 존재 여부는 추가로 공개하지 않습니다.</p>
        <Link href="/" className="primary-action">
          수업마침 처음으로
        </Link>
      </main>
    </PublicFrame>
  );
}

function PublicFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="public-page">
      <PublicHeader />
      {children}
      <footer className="public-footer">
        <span>수업마침 · MUNOPS</span>
        <nav aria-label="정책 메뉴">
          <Link href="/privacy">개인정보 처리 안내</Link>
          <Link href="/terms">이용 조건</Link>
          <Link href="/status">서비스 상태</Link>
        </nav>
        <small>
          공개 가입 운영 중 · 결제·외부 메시지·외부 상담 공급자는 비활성
        </small>
      </footer>
    </div>
  );
}
function PolicySection({
  id,
  number,
  title,
  children,
}: {
  id?: string;
  number: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id}>
      <span>{number}</span>
      <article>
        <h2>{title}</h2>
        {children}
      </article>
    </section>
  );
}
function caseState(value: SupportCase["state"]) {
  return {
    received: "접수됨",
    pending_delivery: "외부 전달 대기",
    human_required: "사람 확인 필요",
    answer_drafted: "답변 초안",
    resolved: "처리됨",
  }[value];
}
function receiptState(value: SupportCase["providerState"]) {
  return {
    draft: "초안",
    pending: "대기",
    accepted: "접수",
    delivered: "전달",
    failed: "실패",
    not_applicable: "해당 없음",
  }[value];
}
function purposeLabel(value: string) {
  return (
    { attendance: "출결", makeup: "보강", billing: "수납", learning: "학습" }[
      value
    ] ?? "운영"
  );
}
export type { Role };
